import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Copy, Eye, EyeOff, ExternalLink, Gauge, Route, Share2, SlidersHorizontal, Wifi, WifiOff, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useFleetSocket } from "@/hooks/useFleetSocket";
import { api } from "@/lib/api";
import { FleetMap } from "@/components/FleetMap";
import { Fleet3DMap } from "@/components/Fleet3DMap";
import { VehicleSidebar } from "@/components/VehicleSidebar";
import { VehicleDetailPanel } from "@/components/VehicleDetailPanel";
import { AssignTripDialog } from "@/components/AssignTripDialog";
import { ManualLocationDialog } from "@/components/ManualLocationDialog";
import { ShareLinkDialog } from "@/components/ShareLinkDialog";
import { FleetStatusDialog } from "@/components/FleetStatusDialog";
import { FleetShareDialog } from "@/components/FleetShareDialog";
import { SpeedRangeDialog } from "@/components/SpeedRangeDialog";
import { GovernanceBar } from "@/components/GovernanceBar";
import { HaltPointDialog } from "@/components/HaltPointDialog";
import { TopBar } from "@/components/TopBar";

export default function Dashboard() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [fleetShareOpen, setFleetShareOpen] = useState(false);
  const [speedOpen, setSpeedOpen] = useState(false);
  const [is3D, setIs3D] = useState(false);
  const [haltOpen, setHaltOpen] = useState(false);
  const [geofences, setGeofences] = useState([]);

  const [clientLink, setClientLink] = useState(null);

  const setTripStatus = async (tripId, status) => {
    try {
      await api.patch(`/trips/${tripId}/status`, null, { params: { status } });
      toast.success(`Trip set to ${status.replace("_", " ")}`);
      load();
    } catch {
      toast.error("Could not update trip status");
    }
  };

  const toggleClientLink = async () => {
    try {
      const link = await ensureClientLink();
      const next = !(link.enabled !== false);
      const { data } = await api.patch(`/shares/${link.id}/enabled`, null, { params: { enabled: next } });
      setClientLink(data);
      toast.success(next ? "Client view turned ON" : "Client view turned OFF");
    } catch {
      toast.error("Could not change the client view setting");
    }
  };

  const ensureClientLink = async () => {
    if (clientLink) return clientLink;
    const { data } = await api.get("/shares", { params: { scope: "fleet" } });
    const active = data.find((l) => !l.revoked);
    if (active) {
      setClientLink(active);
      return active;
    }
    const created = await api.post("/shares/fleet", {
      vehicle_ids: vehicles.map((v) => v.id),
      label: "Client view link",
      expires_days: 30,
    });
    setClientLink(created.data);
    return created.data;
  };

  // CLIENT VIEW is a real link - resolve it on load so the button is a direct anchor.
  useEffect(() => {
    if (clientLink || vehicles.length === 0 || user?.role === "viewer") return;
    ensureClientLink().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, clientLink, user]);

  const copyClientLink = async () => {
    try {
      const link = await ensureClientLink();
      await navigator.clipboard.writeText(`${window.location.origin}${link.path}`);
      toast.success("Client link copied");
    } catch {
      toast.error("Could not copy the client link");
    }
  };

  const [settings, setSettings] = useState(null);

  const load = useCallback(async () => {
    const [v, s, g] = await Promise.all([
      api.get("/vehicles"),
      api.get("/settings"),
      api.get("/geofences"),
    ]);
    setVehicles(v.data);
    setSettings(s.data);
    setGeofences(g.data);
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Could not load fleet"));
  }, [load]);

  const onSocketMessage = useCallback((msg) => {
    if (msg.type === "vehicle_removed") {
      setVehicles((prev) => prev.filter((v) => v.id !== msg.vehicle_id));
      return;
    }
    if (msg.type === "alert") {
      toast.warning(msg.alert.message);
      return;
    }
    if (msg.type !== "vehicle_update") return;
    setVehicles((prev) =>
      prev.map((v) =>
        v.id === msg.vehicle.id
          ? { ...msg.vehicle, active_trip: msg.trip !== undefined ? msg.trip : v.active_trip }
          : v
      )
    );
  }, []);

  const connected = useFleetSocket(onSocketMessage);
  const selected = useMemo(() => vehicles.find((v) => v.id === selectedId), [vehicles, selectedId]);

  const select = useCallback((id) => {
    setSelectedId(id);
    setDrawerOpen(false);
  }, []);

  const canDispatch = user?.role === "admin" || user?.role === "dispatcher";
  const isClient = user?.role === "viewer";

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-[#0A0A0A]">
      <TopBar onMenu={() => setDrawerOpen(true)}>
        <span
          className="hidden items-center gap-1.5 border border-white/10 px-2 py-1 text-[10px] tracking-[0.15em] sm:flex"
          data-testid="ws-status"
        >
          {connected ? (
            <>
              <Wifi size={11} className="text-[#00FF66]" /> <span className="text-[#00FF66]">LIVE</span>
            </>
          ) : (
            <>
              <WifiOff size={11} className="text-[#FF3B30]" /> <span className="text-[#FF3B30]">OFFLINE</span>
            </>
          )}
        </span>
        <button
          data-testid="map-3d-toggle-btn"
          onClick={() => setIs3D((p) => !p)}
          title={is3D ? "Switch to flat map" : "Switch to 3D terrain map"}
          className={`flex items-center gap-1.5 border px-3 py-1.5 text-[10px] tracking-[0.2em] transition-colors duration-150 ${
            is3D ? "border-[#00F0FF] text-[#00F0FF]" : "border-white/15 text-white hover:border-[#00F0FF]"
          }`}
        >
          <Box size={12} /> {is3D ? "3D" : "2D"}
        </button>
        {user?.role !== "viewer" && (
          <a
            data-testid="client-view-btn"
            href={clientLink ? clientLink.path : "#"}
            target="_blank"
            rel="noreferrer"
            title={clientLink ? `${window.location.origin}${clientLink.path}` : "preparing client link"}
            className="hidden sm:flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[10px] tracking-[0.2em] text-white transition-colors duration-150 hover:border-[#FFCC00] hover:text-[#FFCC00]"
          >
            <ExternalLink size={12} /> <span className="hidden xl:inline">CLIENT VIEW</span>
          </a>
        )}
        {user?.role !== "viewer" && (
          <button
            data-testid="client-view-toggle-btn"
            onClick={toggleClientLink}
            title={clientLink?.enabled === false ? "Client view is OFF" : "Client view is ON"}
            className={`hidden sm:flex items-center gap-1.5 border px-3 py-1.5 text-[10px] tracking-[0.2em] transition-colors duration-150 ${
              clientLink?.enabled === false
                ? "border-[#FF3B30]/60 text-[#FF3B30]"
                : "border-[#00FF66]/60 text-[#00FF66]"
            }`}
          >
            {clientLink?.enabled === false ? <EyeOff size={12} /> : <Eye size={12} />}
            <span className="hidden xl:inline">
              CLIENT {clientLink?.enabled === false ? "OFF" : "ON"}
            </span>
          </button>
        )}
        {user?.role !== "viewer" && (
          <button
            data-testid="copy-client-view-link-btn"
            onClick={copyClientLink}
            className="hidden items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[10px] tracking-[0.2em] text-white transition-colors duration-150 hover:border-[#00F0FF] hover:text-[#00F0FF] xl:flex"
          >
            <Copy size={12} /> COPY LINK
          </button>
        )}

        {canDispatch && (
          <button
            data-testid="fleet-share-btn"
            title="Share fleet"
            onClick={() => setFleetShareOpen(true)}
            className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[10px] tracking-[0.2em] text-white transition-colors duration-150 hover:border-[#00F0FF] hover:text-[#00F0FF]"
          >
            <Share2 size={12} /> <span className="hidden xl:inline">SHARE FLEET</span>
          </button>
        )}
        {canDispatch && (
          <button
            data-testid="fleet-status-btn"
            title="Set fleet status"
            onClick={() => setStatusOpen(true)}
            className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[10px] tracking-[0.2em] text-white transition-colors duration-150 hover:border-[#FFCC00] hover:text-[#FFCC00]"
          >
            <SlidersHorizontal size={12} /> <span className="hidden xl:inline">SET STATUS</span>
          </button>
        )}
        {canDispatch && (
          <button
            data-testid="speed-range-btn"
            title="Set fleet speed range"
            onClick={() => setSpeedOpen(true)}
            className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[10px] tracking-[0.2em] text-white transition-colors duration-150 hover:border-[#00F0FF] hover:text-[#00F0FF]"
          >
            <Gauge size={12} /> <span className="hidden xl:inline">SPEED RANGE</span>
          </button>
        )}
        {canDispatch && (
          <button
            data-testid="assign-trip-btn"
            title="Assign trip"
            onClick={() => setAssignOpen(true)}
            className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[10px] tracking-[0.2em] text-white transition-colors duration-150 hover:border-[#00F0FF] hover:text-[#00F0FF]"
          >
            <Route size={12} /> <span className="hidden xl:inline">ASSIGN TRIP</span>
          </button>
        )}
      </TopBar>

      <GovernanceBar />

      <div className="relative flex min-h-0 flex-1">
        <aside className="hidden w-[270px] shrink-0 border-r border-white/10 lg:block xl:w-[320px]">
          <VehicleSidebar vehicles={vehicles} selectedId={selectedId} onSelect={select} />
        </aside>

        <main className="relative min-w-0 flex-1">
          {is3D ? (
            <Fleet3DMap
              vehicles={vehicles}
              selectedId={selectedId}
              onSelect={select}
              geofences={geofences}
            />
          ) : (
            <FleetMap
              vehicles={vehicles}
              selectedId={selectedId}
              onSelect={select}
              geofences={geofences}
            />
          )}
        </main>

        {selected && (
          <aside className="hidden w-[290px] shrink-0 border-l border-white/10 lg:block xl:w-[340px]">
            <VehicleDetailPanel
              vehicle={selected}
              onClose={() => setSelectedId(null)}
              onSetLocation={canDispatch ? () => setLocationOpen(true) : null}
              onShare={canDispatch ? () => setShareOpen(true) : null}
              onSetTripStatus={canDispatch ? setTripStatus : null}
              onSetHaltPoint={canDispatch ? () => setHaltOpen(true) : null}
              isClient={isClient}
              supervisor={settings}
            />
          </aside>
        )}
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[1200] lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDrawerOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[85%] max-w-[320px] border-r border-white/10 bg-[#0A0A0A]">
            <button
              data-testid="close-drawer-btn"
              onClick={() => setDrawerOpen(false)}
              className="absolute right-3 top-3 z-10 border border-white/10 p-1.5 text-[#A1A1AA]"
              aria-label="Close roster"
            >
              <X size={14} />
            </button>
            <VehicleSidebar vehicles={vehicles} selectedId={selectedId} onSelect={select} />
          </div>
        </div>
      )}

      {selected && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[1100] h-[62vh] border-t border-white/10 bg-[#0A0A0A] lg:hidden"
          data-testid="mobile-detail-sheet"
        >
          <VehicleDetailPanel
            vehicle={selected}
            onClose={() => setSelectedId(null)}
            onSetLocation={canDispatch ? () => setLocationOpen(true) : null}
            onShare={canDispatch ? () => setShareOpen(true) : null}
            onSetTripStatus={canDispatch ? setTripStatus : null}
            onSetHaltPoint={canDispatch ? () => setHaltOpen(true) : null}
            isClient={isClient}
            supervisor={settings}
          />
        </div>
      )}

      {assignOpen && (
        <AssignTripDialog
          vehicles={vehicles}
          onClose={() => setAssignOpen(false)}
          onCreated={() => {
            setAssignOpen(false);
            load();
          }}
        />
      )}

      {locationOpen && selected && (
        <ManualLocationDialog
          vehicle={selected}
          onClose={() => setLocationOpen(false)}
          onSaved={() => {
            setLocationOpen(false);
            load();
          }}
        />
      )}
      {shareOpen && selected?.active_trip && (
        <ShareLinkDialog
          trip={selected.active_trip}
          vehicle={selected}
          onClose={() => setShareOpen(false)}
        />
      )}
      {statusOpen && (
        <FleetStatusDialog
          vehicles={vehicles}
          onClose={() => setStatusOpen(false)}
          onSaved={() => {
            setStatusOpen(false);
            load();
          }}
        />
      )}
      {haltOpen && selected?.active_trip && (
        <HaltPointDialog
          vehicle={selected}
          onClose={() => setHaltOpen(false)}
          onSaved={() => {
            setHaltOpen(false);
            load();
          }}
        />
      )}
      {fleetShareOpen && <FleetShareDialog vehicles={vehicles} onClose={() => setFleetShareOpen(false)} />}
      {speedOpen && (
        <SpeedRangeDialog
          vehicles={vehicles}
          onClose={() => setSpeedOpen(false)}
          onSaved={() => {
            setSpeedOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}
